import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Tooltip from '@mui/material/Tooltip';

import SettingsIcon from '@mui/icons-material/Settings';
import ReregisterIcon from '@mui/icons-material/Build';

import MultiGauge from '../charts/multigauge';
import Auth from '../../data/auth-service';
import Helpers from '../../libraries/helpers';

const useStyles = makeStyles(theme => ({
    flexRight: {
        flexDirection: 'row-reverse',
    },
    status: {
        borderRadius: '4px',
    },
    nodeStatus: {
        width: '10px',
        borderTopRightRadius: '0px',
        borderBottomRightRadius: '0px',
    },
    good: {
        background: theme.palette.success.main,
    },
    kindaNotGood: {
        background: theme.palette.warning.main,
    },
    notGood: {
        background: theme.palette.error.main,
    },
    dataBox: {
        background: theme.palette.background.paper
    },
    textCenter: {
        textAlign: 'center',
    },
    nodeData: {
        color: theme.palette.secondary.light,
    },
    hidden: {
        display: 'none',
    },
    contentPad: {
        padding: '2px',
    },
    minCardWidth: {
        width: '570px',
    },
    dataNodeContainer: {
        background: theme.palette.background.default,
    },
    datanode: {
        display: 'flex',
        justifyContent: 'space-between',
        maxWidth: '700px',
        minWidth: '500px',
    },
    online: {
        height: '30px',
        marginLeft: '10px',
        padding: '5px 8px',
        borderRadius: '5px',
        background: theme.palette.success.main,
        color: theme.palette.success.contrastText,
    },
    offline: {
        height: '30px',
        marginLeft: '10px',
        padding: '5px 8px',
        borderRadius: '5px',
        background: theme.palette.error.main,
        color: theme.palette.error.contrastText,
    },
}));

const DataNode = (props) => {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [applianceAnchor, setApplianceAnchor] = useState(null);
    const [settingsAnchor, setSettingsAnchor] = useState([]);
    const [status, setStatus] = useState(true);
    const clusterPerms = Auth.permissions.get('Cluster');

    useEffect(() => {
        const s = [...settingsAnchor];
        props.nodeData.forEach(() => { s.push(null) });

        const status = props.nodeData.reduce((a, c) => {
            if (!a || c.offline) {
                return false;
            }
            return true;
        }, true);

        setSettingsAnchor(s);
        setStatus(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.nodeData]);

    const handleOpenSettings = (e, i) => {
        const s = [...settingsAnchor];
        s[i] = e.currentTarget;
        setSettingsAnchor(s);
    };

    const handleCloseSettings = (i) => {
        const s = [...settingsAnchor];
        s[i] = null;
        setSettingsAnchor(s);
    };

    const handleRoleChange = (role, i) => {
        setApplianceAnchor(null);
        handleCloseSettings(i);
        props.onRoleChange(role, props.nodeData[i]);
    };

    const handleOpenApplianceMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setApplianceAnchor(e.currentTarget);
    };

    const handleCloseApplianceMenu = () => {
        setApplianceAnchor(null);
    };

    const handleReregisterAppliance = () => {
        setApplianceAnchor(null);
        props.onReregister(props.nodeData[0]);
    };

    const handleDeleteAppliance = () => {
        setApplianceAnchor(null);
        props.onDelete(props.nodeData[0]);
    };

    return (
        <Grid item className={classes.minCardWidth} xs={12}>
            <Paper elevation={3}>
                <Box className={clsx({
                    [classes.status]: true,
                    [classes.good]: status,
                    [classes.notGood]: !status,
                })}
                pl={2} width='100%'>
                    <Accordion className={classes.dataNodeContainer}>
                        <AccordionSummary>
                            <Box display="flex" justifyContent="space-between" width="100%">
                                <Box mt={1} display="flex">
                                    <Typography variant="h6">{props.nodeData.length ? props.nodeData[0].ip : ''}</Typography>
                                    {props.nodeData && (() => {
                                        let online = 0;
                                        let offline = 0;

                                        props.nodeData.forEach((v) => {
                                            if (Object.prototype.hasOwnProperty.call(v, 'offline') && v.offline) {
                                                offline++;
                                                return;
                                            }
                                            online++;
                                        });

                                        const on = online ? <Box className={classes.online}>{online} Online</Box> : null;
                                        const off = offline ? <Box className={classes.offline}>{offline} Offline</Box> : null;

                                        return (
                                            <Box display="flex">
                                                {on}
                                                {off}
                                            </Box>
                                        );
                                    })()}
                                </Box>
                                {clusterPerms.write && <Box>
                                    <IconButton
                                        aria-label="node menu"
                                        onMouseDown={(e) => { e.stopPropagation() }}
                                        onClick={(e) => {
                                            handleOpenApplianceMenu(e);
                                        }}
                                        size="large"><SettingsIcon /></IconButton>
                                    {props.nodeData[0].needs_reregister && <Tooltip title="Reregister"><IconButton color="secondary" onClick={handleReregisterAppliance} size="large"><ReregisterIcon /></IconButton></Tooltip>}
                                    <Menu
                                        anchorEl={applianceAnchor}
                                        open={Boolean(applianceAnchor)}
                                        onClose={handleCloseApplianceMenu}
                                    >
                                        <MenuItem onClick={(e) => { e.stopPropagation(); handleReregisterAppliance() }}>Reregister Appliance</MenuItem>
                                        <MenuItem onClick={(e) => { e.stopPropagation(); handleDeleteAppliance() }}>Delete Appliance</MenuItem>
                                    </Menu>
                                </Box>}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Grid container spacing={2}>
                                    {props.nodeData.map((v, i) => {
                                        return (
                                            <Grid item xl={6} key={'datanode-node-' + i}>
                                                <Card className={classes.datanode}>
                                                    <Box className={clsx({
                                                        [classes.nodeStatus]: true,
                                                        [classes.good]: !Object.prototype.hasOwnProperty.call(v, 'offline'),
                                                        [classes.notGood]: Object.prototype.hasOwnProperty.call(v, 'offline') && v.offline,
                                                    })}></Box>
                                                    <CardActionArea aria-label={v.http_address + ' node'} onClick={() => { setOpen(!open) }}>
                                                        <Box p={2} className={classes.dataBox}>
                                                            <Grid container spacing={2} alignItems="center" justifyContent="center">
                                                                <Grid container item xs={12}>
                                                                    <Grid container item xs={9}>
                                                                        <Typography>
                                                                            {v.http_address + ' / ' + (() => {
                                                                                const types = [];
                                                                                v.cluster_manager_node ? types.push('cluster manager') : (() => {})();
                                                                                v.data_node ? types.push('data') : (() => {})();
                                                                                v.ingest_node ? types.push('ingest') : (() => {})();
                                                                                return types.join(' + ');
                                                                            })()}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid container item xs={3} className={classes.flexRight}>
                                                                        <Typography>instance: {v.bricata_instance.split('@').pop()}</Typography>
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container item xs={12} className={classes.textCenter}>
                                                                    <Grid item xs={2}>
                                                                        <Box height={32}>Opensearch Version</Box>
                                                                        <Box>{v.version}</Box>
                                                                    </Grid>
                                                                    <Grid item xs={2}>
                                                                        <Box height={32}>NDR Version</Box>
                                                                        <Box>{v.bricata_version}</Box>
                                                                    </Grid>
                                                                    <Grid item xs={2}>
                                                                        <Box height={32}>Model</Box>
                                                                        <Box>{v.bricata_model}</Box>
                                                                    </Grid>
                                                                    <Grid item xs={2}>
                                                                        <Box height={32}>CPU</Box>
                                                                        <Box>{v.cpu_usage + ' %'}</Box>
                                                                    </Grid>
                                                                    <Grid item xs={2}>
                                                                        <Box height={32}>RAM</Box>
                                                                        <Box>{Helpers.formatBytes(v.total_ram)}</Box>
                                                                    </Grid>
                                                                    <Grid item xs={2}>
                                                                        <Box height={32}>Cores</Box>
                                                                        <Box>{v.allocated_cpus}</Box>
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container item xs={12} className={(classes.textCenter, clsx({ [classes.hidden]: !open }))}>
                                                                    <Grid item xs={3}>
                                                                        <MultiGauge
                                                                            label={'Disk'}
                                                                            value={v.disk_usage * 100}
                                                                            small={true}
                                                                            size={80}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={3}>
                                                                        <Box>Total Disk</Box>
                                                                        <Box className={classes.nodeData}>{Helpers.formatBytes(v.total_disk)}</Box>
                                                                    </Grid>
                                                                    <Grid item xs={3}>
                                                                        <Box>Total Heap</Box>
                                                                        <Box className={classes.nodeData}>{Helpers.formatBytes(v.total_heap)}</Box>
                                                                    </Grid>
                                                                    <Grid item xs={3}>
                                                                        <Box>Disk Read</Box>
                                                                        <Box className={classes.nodeData}>{Helpers.formatBytes(v.disk_read_kilobytes, 1, true)}</Box>
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container item xs={12} className={(classes.textCenter, clsx({ [classes.hidden]: !open }))}>
                                                                    <Grid item xs={3}>
                                                                        <MultiGauge
                                                                            label={'Heap'}
                                                                            value={v.heap_usage * 100}
                                                                            small={true}
                                                                            size={80}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={3}>
                                                                        <Box>Disk Write</Box>
                                                                        <Box className={classes.nodeData}>{Helpers.formatBytes(v.disk_write_kilobytes, 1, true)}</Box>
                                                                    </Grid>
                                                                    <Grid item xs={3}>
                                                                        <Box>Transport Rx</Box>
                                                                        <Box className={classes.nodeData}>{Helpers.formatBytes(v.transport_rx_bytes, 1, true)}</Box>
                                                                    </Grid>
                                                                    <Grid item xs={3}>
                                                                        <Box>Transport Tx</Box>
                                                                        <Box className={classes.nodeData}>{Helpers.formatBytes(v.transport_tx_bytes, 1, true)}</Box>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    </CardActionArea>
                                                    {clusterPerms.admin && <CardContent className={classes.contentPad}>
                                                        <IconButton
                                                            aria-label="settings"
                                                            onClick={(e) => { handleOpenSettings(e, i) }}
                                                            size="large"><SettingsIcon /></IconButton>
                                                        <Menu
                                                            id={'settings-menu-' + i}
                                                            anchorEl={settingsAnchor && settingsAnchor[i]}
                                                            open={Boolean(settingsAnchor[i])}
                                                            onClose={() => { handleCloseSettings(i) }}
                                                        >
                                                            <MenuItem onClick={() => { handleRoleChange('cluster_manager', i) }}>{(() => v.cluster_manager_node ? 'Disable' : 'Enable')()} Cluster Manager Role</MenuItem>
                                                            <MenuItem onClick={() => { handleRoleChange('data', i) }}>{v.data_node ? 'Disable' : 'Enable'} Data Role</MenuItem>
                                                        </Menu>
                                                    </CardContent>}
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Paper>
        </Grid>
    );
};

DataNode.propTypes = {
    nodeData: PropTypes.array,
    onRoleChange: PropTypes.func,
    onReregister: PropTypes.func,
    onDelete: PropTypes.func,
};

export default DataNode;
